//////////////////////////////////////
// FACILITIES MAP

import { renderPagination } from '../../../components/Pagination/pagination.js';

(() => {
  'use strict';

  document.addEventListener('DOMContentLoaded', () => {
    const block = document.querySelector('.facilities-map');
    if (!block) return;

    const data = window.facilitiesMapData || [];
    if (!data.length) return;


    //////////////////////////////////////
    // DOM REFS

    const cardList          = block.querySelector('.results-list');
    const cards             = cardList.querySelectorAll('.facility-card');
    const communitySelect   = block.querySelector('.filter-community');
    const servicesSelect    = block.querySelector('.filter-services');
    const nearMeBtn         = block.querySelector('.near-me');
    const acceptingCheckbox = block.querySelector('.filter-accepting');
    const resetBtn          = block.querySelector('.reset-filters');
    const sortAlphaBtn      = block.querySelector('.sort-alpha');
    const sortProximityBtn  = block.querySelector('.sort-proximity');
    const searchInput       = block.querySelector('.search-input');
    const viewListBtn       = block.querySelector('.view-list');
    const viewMapBtn        = block.querySelector('.view-map');
    const mapColumn         = block.querySelector('.map-column');


    //////////////////////////////////////
    // STATE

    let userLocation = window.facilitiesMapUserLocation || null;
    let currentSort  = 'alpha';


    //////////////////////////////////////
    // HELPERS

    function toRad(deg) {
      return deg * Math.PI / 180;
    }

    function getDistance(loc, facility) {
      if (!facility.lat || !facility.lng) return Infinity;
      const R    = 3959; // miles
      const dLat = toRad(facility.lat - loc.lat);
      const dLon = toRad(facility.lng - loc.lng);
      const a    = Math.sin(dLat / 2) ** 2 +
                   Math.cos(toRad(loc.lat)) * Math.cos(toRad(facility.lat)) *
                   Math.sin(dLon / 2) ** 2;
      return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    }


    //////////////////////////////////////
    // FILTER

    function applyFilters() {
      const communityFilter  = communitySelect.value;
      const serviceFilter    = servicesSelect.value;
      const acceptingFilter  = acceptingCheckbox.checked;
      const searchTerm       = searchInput.value.toLowerCase().trim();

      // Filter
      let filtered = data.filter(f => {
        if (communityFilter && !f.communities.some(c => c.id == communityFilter)) return false;
        if (serviceFilter && !f.services.some(s => s.id == serviceFilter)) return false;
        if (acceptingFilter && !f.accepting) return false;
        if (searchTerm) {
          const haystack = (f.name + ' ' + f.address + ' ' + f.city).toLowerCase();
          if (!haystack.includes(searchTerm)) return false;
        }
        return true;
      });

      // Sort
      if (currentSort === 'alpha') {
        filtered.sort((a, b) => a.name.localeCompare(b.name));
      } else if (currentSort === 'proximity' && userLocation) {
        filtered.sort((a, b) => getDistance(userLocation, a) - getDistance(userLocation, b));
      }

      const filteredIds = new Set(filtered.map(f => f.id));

      // Re-order visible cards by appending in sort order
      filtered.forEach(f => {
        const card = cardList.querySelector(`[data-id="${f.id}"]`);
        if (card) {
          card.hidden = false;
          cardList.appendChild(card);
        }
      });

      // Hide non-matching
      cards.forEach(card => {
        const id = parseInt(card.dataset.id, 10);
        if (!filteredIds.has(id)) card.hidden = true;
      });

      // Dispatch event for map
      block.dispatchEvent(new CustomEvent('facilities:filtered', { detail: { filtered } }));

      // Re-paginate if in list mode
      currentPage = 1;
      updatePagination();
    }


    //////////////////////////////////////
    // CARD EXPANSION

    cards.forEach(card => {
      const toggle     = card.querySelector('.expand-toggle');
      const expandable = card.querySelector('.expandable');
      if (!toggle || !expandable) return;

      toggle.addEventListener('click', () => {
        const isOpen = card.classList.contains('is-open');
        card.classList.toggle('is-open', !isOpen);
        toggle.classList.toggle('expanded', !isOpen);

        // Scroll card to top of results list when opening
        if (!isOpen) {
          const resultsList = block.querySelector('.results-list');
          setTimeout(() => {
            resultsList.scrollTo({ top: card.offsetTop - resultsList.offsetTop, behavior: 'smooth' });
          }, 50);
        }
      });
    });


    //////////////////////////////////////
    // EVENTS

    // Filters
    communitySelect.addEventListener('change', applyFilters);
    servicesSelect.addEventListener('change', applyFilters);
    acceptingCheckbox.addEventListener('change', applyFilters);
    searchInput.addEventListener('input', applyFilters);

    // Sort: Alphabetical
    sortAlphaBtn.addEventListener('click', () => {
      currentSort = 'alpha';
      sortAlphaBtn.classList.add('active');
      sortProximityBtn.classList.remove('active');
      applyFilters();
    });

    // Sort: Proximity
    sortProximityBtn.addEventListener('click', () => {
      if (!userLocation) {
        requestGeolocation();
        return;
      }
      currentSort = 'proximity';
      sortProximityBtn.classList.add('active');
      sortAlphaBtn.classList.remove('active');
      applyFilters();
    });

    // Near Me
    const geoTargetLocation = window.facilitiesMapUserLocation || null;

    if (nearMeBtn) {
      nearMeBtn.addEventListener('click', () => {
        if (nearMeBtn.classList.contains('active')) {
          // Deactivate
          nearMeBtn.classList.remove('active');
          if (currentSort === 'proximity') {
            currentSort = 'alpha';
            sortAlphaBtn.classList.add('active');
            sortProximityBtn.classList.remove('active');
          }
          applyFilters();
          block.dispatchEvent(new CustomEvent('facilities:zoomall'));
          return;
        }

        // Use GeoTarget location if available, otherwise browser geolocation
        if (geoTargetLocation) {
          userLocation = geoTargetLocation;
          activateNearMe();
        } else {
          requestGeolocation();
        }
      });
    }

    function activateNearMe() {
      if (nearMeBtn) nearMeBtn.classList.add('active');
      sortProximityBtn.disabled = false;
      currentSort = 'proximity';
      sortProximityBtn.classList.add('active');
      sortAlphaBtn.classList.remove('active');
      applyFilters();
      block.dispatchEvent(new CustomEvent('facilities:zoomuser', { detail: userLocation }));
    }

    // Reset
    resetBtn.addEventListener('click', () => {
      communitySelect.value    = '';
      servicesSelect.value     = '';
      acceptingCheckbox.checked = false;
      searchInput.value        = '';
      if (nearMeBtn) nearMeBtn.classList.remove('active');
      currentSort = 'alpha';
      sortAlphaBtn.classList.add('active');
      sortProximityBtn.classList.remove('active');
      sortProximityBtn.disabled = !userLocation;
      applyFilters();
    });

    // View toggle
    const isLowData        = block.classList.contains('low-data');
    const contentGrid      = block.querySelector('.content');
    const pagination        = block.querySelector('.pagination');
    const ITEMS_PER_PAGE    = 18;
    let currentPage         = 1;

    // Low data mode: force list view permanently
    if (isLowData) {
      contentGrid.classList.add('list-mode');
      pagination.style.display = 'flex';
    }

    if (viewListBtn) {
      viewListBtn.addEventListener('click', () => {
        viewListBtn.classList.add('active');
        if (viewMapBtn) viewMapBtn.classList.remove('active');
        contentGrid.classList.add('list-mode');
        cards.forEach(c => c.classList.remove('highlighted'));
        destroyMap();
        pagination.style.display = 'flex';
        currentPage = 1;
        updatePagination();
      });
    }

    if (viewMapBtn) {
      viewMapBtn.addEventListener('click', () => {
        viewMapBtn.classList.add('active');
        if (viewListBtn) viewListBtn.classList.remove('active');
        contentGrid.classList.remove('list-mode');
        pagination.style.display = 'none';
        // Reset all card state
        cards.forEach(card => {
          card.style.display = '';
          card.classList.remove('highlighted', 'is-open');
          const toggle = card.querySelector('.expand-toggle');
          if (toggle) toggle.classList.remove('expanded');
        });
        applyFilters();
        // Rebuild map from scratch
        setTimeout(() => initMap(), 50);
      });
    }


    //////////////////////////////////////
    // PAGINATION

    function getVisibleCards() {
      return Array.from(cards).filter(c => !c.hidden);
    }

    function updatePagination() {
      if (!contentGrid.classList.contains('list-mode')) return;

      const visible    = getVisibleCards();
      const totalItems = visible.length;
      const totalPages = Math.max(1, Math.ceil(totalItems / ITEMS_PER_PAGE));

      if (currentPage > totalPages) currentPage = totalPages;

      const start = (currentPage - 1) * ITEMS_PER_PAGE;
      const end   = start + ITEMS_PER_PAGE;

      // Show/hide cards for current page
      visible.forEach((card, i) => {
        card.style.display = (i >= start && i < end) ? '' : 'none';
      });

      renderPagination({
        container:   pagination,
        currentPage,
        totalItems,
        perPage:     ITEMS_PER_PAGE,
        label:       'Facilities',
        scroll:      false,
        onPageChange(page) { currentPage = page; updatePagination(); }
      });
    }


    //////////////////////////////////////
    // GEOLOCATION

    function requestGeolocation() {
      if (!navigator.geolocation || !nearMeBtn) return;
      nearMeBtn.classList.add('loading');

      navigator.geolocation.getCurrentPosition(
        (pos) => {
          userLocation = { lat: pos.coords.latitude, lng: pos.coords.longitude };
          nearMeBtn.classList.remove('loading');
          activateNearMe();
        },
        () => {
          nearMeBtn.classList.remove('loading');
        }
      );
    }


    //////////////////////////////////////
    // MAP (LEAFLET)

    const mapEl = document.getElementById('facilities-leaflet-map');
    const typeColors = {
      'hospital':         '#D72423',
      'clinic':           '#007CA2',
      'community-health': '#6AA342',
    };
    const typeIcons = window.facilitiesMapIcons || {};

    let map = null;
    let markers = {};
    let mapEventController = null;

    function buildPopup(f) {
      const icon = typeIcons[f.type] || '';
      const phone = f.phone
        ? `<a href="tel:${f.phone.replace(/[^0-9+]/g, '')}" class="popup-phone">${f.phone}</a>`
        : '';
      return `
        <div class="map-popup">
          <div class="popup-icon type-${f.type}">${icon}</div>
          <div class="popup-content">
            <a href="${f.permalink}" class="popup-name">${f.name}</a>
            <span class="popup-badge type-${f.type}">${f.typeLabel}</span>
            ${f.address ? `<p class="popup-address">${f.address}</p>` : ''}
            ${phone}
          </div>
        </div>
      `;
    }

    function destroyMap() {
      if (mapEventController) {
        mapEventController.abort();
        mapEventController = null;
      }
      if (map) {
        map.remove();
        map = null;
        markers = {};
      }
    }

    function initMap() {
      if (!mapEl || typeof L === 'undefined') return;

      destroyMap();

      mapEventController = new AbortController();
      const signal = mapEventController.signal;

      map = L.map(mapEl, {
        scrollWheelZoom: false,
      }).setView([58.3, -134.4], 7);

      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
        maxZoom: 18,
      }).addTo(map);

      // Create markers
      const allMarkers = L.featureGroup();

      data.forEach(f => {
        if (!f.lat || !f.lng) return;

        const color  = typeColors[f.type] || '#6AA342';
        const marker = L.circleMarker([f.lat, f.lng], {
          radius:      10,
          fillColor:   color,
          color:       '#fff',
          weight:      2,
          opacity:     1,
          fillOpacity: 0.9,
        });

        marker.bindPopup(buildPopup(f), { className: 'facility-popup', maxWidth: 280 });

        marker.on('click', () => {
          const card = cardList.querySelector(`[data-id="${f.id}"]`);
          if (card && !card.hidden) {
            cards.forEach(c => c.classList.remove('highlighted'));
            card.classList.add('highlighted');
            card.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
          }
        });

        markers[f.id] = marker;
        allMarkers.addLayer(marker);
      });

      allMarkers.addTo(map);

      if (allMarkers.getLayers().length) {
        map.fitBounds(allMarkers.getBounds().pad(0.1));
      }

      // Ensure map is properly sized after container is visible
      setTimeout(() => {
        map.invalidateSize();
        if (allMarkers.getLayers().length) {
          map.fitBounds(allMarkers.getBounds().pad(0.1));
        }
      }, 300);

      // Event listeners scoped to this map instance
      block.addEventListener('facilities:filtered', (e) => {
        const filteredSet = new Set(e.detail.filtered.map(f => f.id));

        Object.entries(markers).forEach(([id, marker]) => {
          const numId = parseInt(id, 10);
          if (filteredSet.has(numId)) {
            if (!map.hasLayer(marker)) map.addLayer(marker);
          } else {
            if (map.hasLayer(marker)) map.removeLayer(marker);
          }
        });

        const visible = Object.entries(markers)
          .filter(([id]) => filteredSet.has(parseInt(id, 10)))
          .map(([, m]) => m);

        if (visible.length) {
          map.fitBounds(L.featureGroup(visible).getBounds().pad(0.1));
        }

        updateGoogleMapsLink(e.detail.filtered);
      }, { signal });

      block.addEventListener('facilities:zoomuser', (e) => {
        const loc = e.detail;
        if (loc && loc.lat && loc.lng) {
          map.flyTo([loc.lat, loc.lng], 10, { duration: 1 });
        }
      }, { signal });

      block.addEventListener('facilities:zoomall', () => {
        const visible = Object.values(markers).filter(m => map.hasLayer(m));
        if (visible.length) {
          map.flyToBounds(L.featureGroup(visible).getBounds().pad(0.1), { duration: 1 });
        }
      }, { signal });

      // "View on Map" buttons
      block.querySelectorAll('.view-on-map-btn').forEach(btn => {
        btn.addEventListener('click', () => {
          const id     = parseInt(btn.dataset.id, 10);
          const marker = markers[id];
          if (!marker) return;

          map.setView(marker.getLatLng(), 14);
          marker.openPopup();

          if (window.innerWidth <= 900) {
            viewMapBtn.click();
          }
        }, { signal });
      });

      function updateGoogleMapsLink(filtered) {
        const gmapBtn = block.querySelector('.google-maps-btn');
        if (!gmapBtn) return;

        if (filtered.length === 1 && filtered[0].lat && filtered[0].lng) {
          gmapBtn.href = `https://www.google.com/maps/search/?api=1&query=${filtered[0].lat},${filtered[0].lng}`;
        } else {
          gmapBtn.href = 'https://www.google.com/maps/search/SEARHC/@61.2,-149.9,5z';
        }
      }
    }

    // Init map on load
    initMap();


    //////////////////////////////////////
    // INIT

    // If user location is available, default to proximity sort
    if (userLocation) {
      if (sortProximityBtn) sortProximityBtn.disabled = false;
      currentSort = 'proximity';
      if (sortProximityBtn) sortProximityBtn.classList.add('active');
      if (sortAlphaBtn) sortAlphaBtn.classList.remove('active');
    }

    applyFilters();
  });
})();
